import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { either } from '@guardian/types';
import { parseBrazeEpicParams, } from '@root/src/web/lib/braze/parseBrazeEpicParams';
import { getBrazeMetaFromUrlFragment } from '@root/src/web/lib/braze/forceBrazeMessage';
import { useOnce } from '@root/src/web/lib/useOnce';
import { joinUrl } from '@root/src/lib/joinUrl';
import { useHasBeenSeen } from '@root/src/web/lib/useHasBeenSeen';
import { submitComponentEvent } from '@root/src/web/browser/ophan/ophan';
import { setAutomat } from '@root/src/web/lib/setAutomat';
import { MODULES_VERSION } from '@root/src/web/lib/contributions';
const wrapperMargins = css `
	margin: 18px 0;
`;
const EPIC_COMPONENT_PATH = `/modules/${MODULES_VERSION}/epics/ContributionsEpic.js`;
const COMPONENT_TYPE = 'RETENTION_EPIC';
export const canShow = async (brazeMessagesPromise) => {
    const forcedBrazeMeta = getBrazeMetaFromUrlFragment();
    if (forcedBrazeMeta) {
        return {
            show: true,
            meta: forcedBrazeMeta,
        };
    }
    try {
        const brazeMessages = await brazeMessagesPromise;
        const message = await brazeMessages.getMessageForEndOfArticle();
        return {
            show: true,
            meta: {
                dataFromBraze: message.extras,
                logImpressionWithBraze: () => {
                    message.logImpression();
                },
            },
        };
    }
    catch (e) {
        return { show: false };
    }
};
const buildEpicProps = (dataFromBraze, countryCode) => {
    const variantResult = parseBrazeEpicParams(dataFromBraze);
    return either((err) => {
        const msg = `Not rendering Braze epic - there are props missing: ${err}`;
        window.guardian.modules.sentry.reportError(new Error(msg), 'braze-epic');
        return null;
    }, (variant) => {
        const pageTracking = {
            ophanPageId: window.guardian.config.ophan.pageViewId,
            platformId: 'GUARDIAN_WEB',
            clientName: 'dcr',
            referrerUrl: window.location.origin + window.location.pathname,
        };
        const tracking = {
            ...pageTracking,
        };
        return { variant, tracking, numArticles: 0, countryCode };
    })(variantResult);
};
const BrazeEpic = ({ contributionsServiceUrl, meta, countryCode, }) => {
    const [Epic, setEpic] = useState();
    const [hasBeenSeen, setNode] = useHasBeenSeen({
        rootMargin: '-18px',
        threshold: 0,
        debounce: true,
    });
    const epicRef = useRef(null);
    useOnce(() => {
        setAutomat();
        const componentUrl = joinUrl([
            contributionsServiceUrl,
            EPIC_COMPONENT_PATH,
        ]);
        window
            .guardianPolyfilledImport(componentUrl)
            .then((epicModule) => {
            setEpic(() => epicModule.ContributionsEpic); // useState requires functions to be wrapped
        })
            .catch((error) => {
            window.guardian.modules.sentry.reportError(error, 'braze-epic');
        });
    }, [contributionsServiceUrl, meta]);
    useOnce(() => {
        submitComponentEvent({
            component: {
                componentType: COMPONENT_TYPE,
                id: meta.dataFromBraze.ophanComponentId,
            },
            action: 'INSERT',
        });
    }, [meta?.dataFromBraze, epicRef.current]);
    useEffect(() => {
        if (hasBeenSeen && meta && meta.dataFromBraze) {
            meta.logImpressionWithBraze();
            // Log VIEW event with Ophan
            submitComponentEvent({
                component: {
                    componentType: COMPONENT_TYPE,
                    id: meta.dataFromBraze.ophanComponentId,
                },
                action: 'VIEW',
            });
        }
    }, [hasBeenSeen, meta]);
    if (Epic && meta.dataFromBraze) {
        // This will come from Braze via the meta from canShow
        const props = buildEpicProps(meta.dataFromBraze, countryCode);
        if (props) {
            return (_jsx("div", Object.assign({ ref: setNode, css: wrapperMargins }, { children: _jsx("div", Object.assign({ ref: epicRef }, { children: _jsx(Epic, Object.assign({}, props), void 0) }), void 0) }), void 0));
        }
    }
    return null;
};
export const MaybeBrazeEpic = ({ contributionsServiceUrl, meta, countryCode, }) => {
    const componentName = meta.dataFromBraze?.componentName;
    if (componentName === 'Epic') {
        return (_jsx(BrazeEpic, { contributionsServiceUrl: contributionsServiceUrl, meta: meta, countryCode: countryCode }, void 0));
    }
    return null;
};
